/**
 * Admin · Access Requests — approve/deny self-registered users (ADR-0015).
 */
import { useState } from 'react';
import { BellOff, BellRing, Check, Loader2, Power, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  RoleSchema,
  type AccessRequestItem,
  type AdminUser,
  type Role,
} from '@vari-crew/schemas';
import {
  useAccessRequests,
  useAdminUsers,
  useApproveAccessRequest,
  useDenyAccessRequest,
  useUpdateUser,
} from '@/api/access';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { AddFromDirectoryDialog } from '@/components/admin/AddFromDirectoryDialog';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';

type Tab = 'pending' | 'active' | 'denied';
const TABS: Tab[] = ['pending', 'active', 'denied'];

export function AccessRequests() {
  const [tab, setTab] = useState<Tab>('pending');
  const [addOpen, setAddOpen] = useState(false);
  const { data, isLoading, error } = useAccessRequests(tab);
  const approve = useApproveAccessRequest();
  const deny = useDenyAccessRequest();
  const busy = approve.isPending || deny.isPending;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Users &amp; Access</h1>
          <p className="text-sm text-ink-dim">
            Approve access requests, and manage every user&apos;s role and status.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} className="shrink-0">
          <UserPlus className="w-4 h-4" /> Add from directory
        </Button>
      </header>

      <AddFromDirectoryDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <div className="flex gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'px-3 py-1.5 text-sm rounded-md border capitalize transition-colors',
              tab === t
                ? 'border-ink bg-ink text-bg'
                : 'border-line text-ink-dim hover:bg-fill-soft',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading && (
        <Card>
          <CardContent className="py-10 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </CardContent>
        </Card>
      )}
      {error && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="border-b border-line bg-fill-soft text-xs uppercase tracking-wide text-ink-faint">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">User</th>
                  <th className="px-4 py-2 text-left font-medium">Requested</th>
                  <th className="px-4 py-2 text-left font-medium">Justification</th>
                  <th className="px-4 py-2 text-right font-medium">
                    {tab === 'pending' ? 'Decision' : 'Status'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.data.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-center text-ink-dim">
                      No {tab} requests.
                    </td>
                  </tr>
                )}
                {data.data.map((item) => (
                  <RequestRow
                    key={item.id}
                    item={item}
                    busy={busy}
                    onApprove={(grantRole) =>
                      approve.mutate(
                        { userId: item.id, role: grantRole },
                        {
                          onSuccess: () =>
                            toast.success(`${item.display_name} approved as ${grantRole}`),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                    onDeny={() =>
                      deny.mutate(item.id, {
                        onSuccess: () => toast.success(`${item.display_name} denied`),
                        onError: (e) => toast.error(e.message),
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AllUsersSection />
    </div>
  );
}

function RequestRow({
  item,
  onApprove,
  onDeny,
  busy,
}: {
  item: AccessRequestItem;
  onApprove: (role: Role) => void;
  onDeny: () => void;
  busy: boolean;
}) {
  const [role, setRole] = useState<Role>(item.requested_role ?? item.role);
  return (
    <tr className="border-b border-line last:border-0 align-top">
      <td className="px-4 py-3">
        <div className="font-medium">{item.display_name}</div>
        <div className="text-xs text-ink-faint">{item.email}</div>
        <div className="text-[11px] text-ink-faint">{formatDate(item.created_at)}</div>
      </td>
      <td className="px-4 py-3 capitalize">{item.requested_role ?? '—'}</td>
      <td className="px-4 py-3 text-ink-dim max-w-[260px]">
        {item.justification || <span className="text-ink-faint">—</span>}
      </td>
      <td className="px-4 py-3">
        {item.status === 'pending' ? (
          <div className="flex items-center justify-end gap-2">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              disabled={busy}
              className="px-2 py-1 text-xs border border-line rounded bg-bg"
            >
              {RoleSchema.options.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <Button size="sm" onClick={() => onApprove(role)} disabled={busy}>
              <Check className="w-3.5 h-3.5" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={onDeny} disabled={busy}>
              <X className="w-3.5 h-3.5" /> Deny
            </Button>
          </div>
        ) : (
          <div className="text-right text-xs capitalize">
            {item.status}
            {item.role && <span className="text-ink-faint"> · {item.role}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}

function AllUsersSection() {
  const { data, isLoading, error } = useAdminUsers();
  const { data: me } = useCurrentUser();
  const update = useUpdateUser();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">All users · roles &amp; status</CardTitle>
        <p className="text-xs text-ink-dim mt-1 leading-relaxed">
          <span className="font-medium">Notifications</span> is a per-person override for the
          org-wide &ldquo;Divert to admins&rdquo; mode (set in the Notifications log).{' '}
          <span className="font-medium text-status-complete-fg">Live</span> = this person gets their
          real emails now, even while the org is diverting.{' '}
          <span className="font-medium">Diverted</span> = their email is held and rerouted to the
          admins. When the org mode is &ldquo;Real recipients,&rdquo; everyone gets real email
          regardless of this toggle.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="border-y border-line bg-fill-soft text-xs uppercase tracking-wide text-ink-faint">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">User</th>
                  <th className="px-4 py-2 text-left font-medium">Role</th>
                  <th className="px-4 py-2 text-left font-medium">Notifications</th>
                  <th className="px-4 py-2 text-right font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.data ?? []).map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isSelf={u.id === me?.id}
                    busy={update.isPending}
                    onRole={(role) =>
                      update.mutate(
                        { userId: u.id, body: { role } },
                        {
                          onSuccess: () =>
                            toast.success(`${u.display_name} → ${role}`),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                    onStatus={(status) =>
                      update.mutate(
                        { userId: u.id, body: { status } },
                        {
                          onSuccess: () =>
                            toast.success(
                              `${u.display_name} ${status === 'active' ? 'activated' : 'deactivated'}`,
                            ),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                    onLive={(live) =>
                      update.mutate(
                        { userId: u.id, body: { notifications_live: live } },
                        {
                          onSuccess: () =>
                            toast.success(
                              `${u.display_name} notifications ${live ? 'live' : 'diverted'}`,
                            ),
                          onError: (e) => toast.error(e.message),
                        },
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UserRow({
  user,
  isSelf,
  busy,
  onRole,
  onStatus,
  onLive,
}: {
  user: AdminUser;
  isSelf: boolean;
  busy: boolean;
  onRole: (role: Role) => void;
  onStatus: (status: 'active' | 'denied') => void;
  onLive: (live: boolean) => void;
}) {
  const active = user.is_active;
  // An admin can't demote or deactivate their own account (the server
  // refuses it too — this just stops the controls short of a 400). Editing
  // your own non-admin fields isn't a thing here, so for self we lock both.
  const selfLocked = isSelf && user.role === 'admin';
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-4 py-3">
        <div className="font-medium">
          {user.display_name}
          {isSelf && (
            <span className="ml-1.5 text-[10px] text-ink-faint">(you)</span>
          )}
        </div>
        <div className="text-xs text-ink-faint">{user.email}</div>
      </td>
      <td className="px-4 py-3">
        <select
          value={user.role}
          disabled={busy || selfLocked}
          title={selfLocked ? "You can't change your own admin role" : undefined}
          onChange={(e) => onRole(e.target.value as Role)}
          className="px-2 py-1 text-xs border border-line rounded bg-bg capitalize disabled:opacity-60"
        >
          {RoleSchema.options.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onLive(!user.notifications_live)}
          title={
            user.notifications_live
              ? 'Receiving real notifications'
              : 'Notifications diverted to admins while the org is in divert mode'
          }
          className={cn(user.notifications_live && 'text-status-complete-fg')}
        >
          {user.notifications_live ? (
            <BellRing className="w-3.5 h-3.5" />
          ) : (
            <BellOff className="w-3.5 h-3.5" />
          )}
          {user.notifications_live ? 'Live' : 'Diverted'}
        </Button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <span
            className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full border capitalize',
              active
                ? 'border-line text-status-complete-fg'
                : 'border-line text-ink-faint',
            )}
          >
            {active ? 'active' : user.status}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy || selfLocked}
            title={selfLocked ? "You can't deactivate your own admin account" : undefined}
            onClick={() => onStatus(active ? 'denied' : 'active')}
          >
            <Power className="w-3.5 h-3.5" />
            {active ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </td>
    </tr>
  );
}
